import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import Editor from 'src/common/components/Editor';
import { LanguageKeys } from 'src/common/constants/languages';
import { signupTextPresentationForLegacyKey } from 'src/helpers/signupPageLayoutUtils';
import {
  ICompetitionsListBlock,
  IImageBlock,
  IMapBlock,
  IPageBlock,
  IResultsBlock,
  ITextBlock,
  ITrackMapBlock,
  TextPresentation,
} from 'src/interfaces/PageBlock';
import { ITrackMap } from 'src/interfaces/TrackMap';
import ImageBlockEditorPanel from 'src/pages/Dashboard/System/Pages/components/ImageBlockEditorPanel';
import MapBlockEditorPanel from 'src/pages/Dashboard/System/Pages/components/MapBlockEditorPanel';
import PageBlockEditorFields from 'src/pages/Dashboard/System/Pages/components/PageBlockEditorFields';

interface PageBlockFieldsPanelProps {
  block: IPageBlock;
  contentKey: string;
  pageContentId?: string;
  selectedEditorLang: LanguageKeys;
  setSelectedEditorLang: (lang: LanguageKeys) => void;
  trackMaps: ITrackMap[];
  isSignupShell?: boolean;
  onUpdate: (id: string, patch: Partial<IPageBlock>) => void;
}

export default function PageBlockFieldsPanel({
  block,
  contentKey,
  pageContentId,
  selectedEditorLang,
  setSelectedEditorLang,
  trackMaps,
  isSignupShell = false,
  onUpdate,
}: PageBlockFieldsPanelProps) {
  const { t } = useTranslation();

  const update = (patch: Partial<IPageBlock>) => onUpdate(block.id, patch);

  if (block.type === 'columns') {
    return null;
  }

  switch (block.type) {
    case 'logo':
    case 'calendar':
    case 'countdown':
    case 'button':
      return (
        <PageBlockEditorFields
          block={block}
          selectedEditorLang={selectedEditorLang}
          setSelectedEditorLang={setSelectedEditorLang}
          contentKey={contentKey}
          pageContentId={pageContentId}
          onChange={update}
        />
      );
    case 'text': {
      const textBlock = block as ITextBlock;
      const presentation =
        textBlock.textPresentation ?? signupTextPresentationForLegacyKey(textBlock.legacyKey);
      return (
        <Box>
          {isSignupShell ? (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>{t('system.pages.textPresentation.label')}</InputLabel>
              <Select
                label={t('system.pages.textPresentation.label')}
                value={presentation}
                onChange={e =>
                  update({
                    textPresentation: e.target.value as TextPresentation,
                  } as Partial<ITextBlock>)
                }
              >
                <MenuItem value="inline">{t('system.pages.textPresentation.inline')}</MenuItem>
                <MenuItem value="embedded">{t('system.pages.textPresentation.embedded')}</MenuItem>
                <MenuItem value="modal">{t('system.pages.textPresentation.modal')}</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {t('system.pages.textPresentation.helper')}
              </Typography>
            </FormControl>
          ) : null}
          {isSignupShell && textBlock.legacyKey === 'successMessage' ? (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {t('race.signUp.contentShownWhenSignupSuccessfulTooltip')}
            </Typography>
          ) : null}
          <Editor
            value={textBlock.i18n[selectedEditorLang] ?? ''}
            onChange={v =>
              update({
                i18n: { ...textBlock.i18n, [selectedEditorLang]: v },
              } as Partial<ITextBlock>)
            }
            contentType="pageContent"
            contentId={pageContentId || contentKey}
            fieldName="content"
          />
        </Box>
      );
    }
    case 'divider':
      return null;
    case 'trackMap': {
      const tm = block as ITrackMapBlock;
      return (
        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
          <InputLabel>{t('system.pages.trackMap')}</InputLabel>
          <Select
            label={t('system.pages.trackMap')}
            value={tm.trackMapId}
            onChange={e => update({ trackMapId: e.target.value })}
          >
            {trackMaps.map(m => (
              <MenuItem key={m.id} value={m.id}>
                {m.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    case 'map': {
      const mapBlock = block as IMapBlock;
      return <MapBlockEditorPanel mapBlock={mapBlock} onUpdate={update} />;
    }
    case 'image': {
      const imageBlock = block as IImageBlock;
      return (
        <ImageBlockEditorPanel
          block={imageBlock}
          contentKey={contentKey}
          pageContentId={pageContentId}
          selectedEditorLang={selectedEditorLang}
          setSelectedEditorLang={setSelectedEditorLang}
          onChange={update}
        />
      );
    }
    case 'sponsors':
      return (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('system.pages.sponsorsGridBlock')}
        </Typography>
      );
    case 'signupBreakdown':
      return (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('system.pages.signupBreakdownBlockHint')}
        </Typography>
      );
    case 'competitionsList': {
      const cl = block as ICompetitionsListBlock;
      return (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('system.pages.competitionsListBlockHint')}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id={`competitions-list-columns-${block.id}`}>
              {t('system.pages.competitionsListColumnCount')}
            </InputLabel>
            <Select
              labelId={`competitions-list-columns-${block.id}`}
              value={cl.columnCount ?? 4}
              label={t('system.pages.competitionsListColumnCount')}
              onChange={e => update({ columnCount: Number(e.target.value) as 2 | 4 })}
            >
              <MenuItem value={4}>{t('system.pages.competitionsListColumns4')}</MenuItem>
              <MenuItem value={2}>{t('system.pages.competitionsListColumns2')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      );
    }
    case 'results': {
      const rb = block as IResultsBlock;
      return (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
          <TextField
            size="small"
            type="number"
            label={t('system.pages.defaultYear')}
            value={rb.defaultYear ?? ''}
            onChange={e =>
              update({
                defaultYear: e.target.value ? Number(e.target.value) : undefined,
              })
            }
          />
        </Box>
      );
    }
    default:
      return null;
  }
}
